import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Mail, Send, Eye, Code, RotateCcw, Loader2, CheckCircle, Save,
  ChevronLeft, Clipboard, AlertTriangle, Sparkles, FileText, Lock,
  ShieldCheck, Pencil, Info, X, Calendar, FlaskConical, Zap, RefreshCcw,
} from "lucide-react";
import { ScheduledMessagesManager } from "./ScheduledMessagesManager";

const emailEditorDarkStyle = `
.email-editor-dark, .email-editor-dark * {
  color: #e5e7eb !important;
}
.email-editor-dark a { color: #93c5fd !important; }
.email-editor-dark strong, .email-editor-dark b { color: #f3f4f6 !important; }
.email-editor-dark div[style], .email-editor-dark blockquote,
.email-editor-dark table, .email-editor-dark td, .email-editor-dark th,
.email-editor-dark tr, .email-editor-dark p[style], .email-editor-dark span[style] {
  background-color: transparent !important;
  background: transparent !important;
  border-color: #4b5563 !important;
}
`;

import { EmailAnalyticsDashboard } from "./EmailAnalyticsDashboard";
import { ABTestManager } from "./ABTestManager";
import { EngagementScoring } from "./EngagementScoring";
import { ReengagementManager } from "./ReengagementManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATE_ICONS = {
  welcome: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  verification: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  password_reset: "bg-red-500/10 text-red-600 border-red-500/20",
  order_confirmation: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  admin_notification: "bg-purple-500/10 text-purple-600 border-purple-500/20",
  newsletter: "bg-pink-500/10 text-pink-600 border-pink-500/20",
  inquiry_auto_response: "bg-teal-500/10 text-teal-600 border-teal-500/20",
};

const CLASS_STYLES = {
  strict_fixed: { label: "Strict Fixed", icon: Lock, bg: "bg-red-500/10", text: "text-red-600", border: "border-red-500/20", desc: "System-generated, non-editable" },
  fixed: { label: "Fixed", icon: ShieldCheck, bg: "bg-amber-500/10", text: "text-amber-600", border: "border-amber-500/20", desc: "System-generated, admin-overridable" },
  dynamic: { label: "Dynamic", icon: Pencil, bg: "bg-blue-500/10", text: "text-blue-600", border: "border-blue-500/20", desc: "Admin-controlled placeholder" },
};

export function EmailSettingsTab({ darkMode: dm, viewOnly }) {
  const [subTab, setSubTab] = useState("templates"); // templates | scheduled
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editorMode, setEditorMode] = useState("visual");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [dismissedWarnings, setDismissedWarnings] = useState(new Set());

  const headers = { Authorization: `Bearer ${localStorage.getItem("gmp_token")}` };
  const editorRef = useRef(null);
  const iframeRef = useRef(null);
  const validateTimerRef = useRef(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/email-templates`, { headers });
      setTemplates(res.data);
    } catch (err) {
      toast.error("Failed to load email templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const selectTemplate = (key) => {
    const t = templates.find(t => t.template_key === key);
    if (t) {
      setSelectedKey(key);
      setEditData({ ...t });
      setShowPreview(false);
      setEditorMode("visual");
      setValidationWarnings([]);
      setDismissedWarnings(new Set());
    }
  };

  // Real-time validation with debounce
  const runValidation = useCallback(async (key, subject, html) => {
    try {
      const res = await axios.post(`${API}/admin/email-templates/${key}/validate`, { subject, html_content: html }, { headers });
      setValidationWarnings(res.data.warnings || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (!selectedKey || !editData) return;
    if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
    validateTimerRef.current = setTimeout(() => {
      runValidation(selectedKey, editData.subject, editData.html_content);
    }, 800);
    return () => clearTimeout(validateTimerRef.current);
  }, [selectedKey, editData?.subject, editData?.html_content, runValidation]);

  const handleSave = async () => {
    if (!editData || viewOnly) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/email-templates/${selectedKey}`, editData, { headers });
      if (res.data.warnings?.length > 0) {
        toast.warning(`Template saved with ${res.data.warnings.length} warning(s)`);
      } else {
        toast.success("Template saved successfully");
      }
      fetchTemplates();
    } catch (err) {
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedKey || viewOnly) return;
    if (!window.confirm("Reset this template to its default? Your custom changes will be lost.")) return;
    try {
      await axios.post(`${API}/admin/email-templates/${selectedKey}/reset`, {}, { headers });
      toast.success("Template reset to default");
      const res = await axios.get(`${API}/admin/email-templates/${selectedKey}`, { headers });
      setEditData(res.data);
      setValidationWarnings([]);
      fetchTemplates();
    } catch (err) {
      toast.error("Failed to reset template");
    }
  };

  const handlePreview = async () => {
    if (!editData) return;
    try {
      const res = await axios.post(`${API}/admin/email-templates/${selectedKey}/preview`, {
        html_content: editData.html_content,
        subject: editData.subject,
      }, { headers });
      setPreviewHtml(res.data.html);
      setPreviewSubject(res.data.subject);
      setShowPreview(true);
    } catch (err) {
      toast.error("Failed to generate preview");
    }
  };

  const handleTestSend = async () => {
    if (!testEmail.trim() || !selectedKey) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/email-templates/test-send`, {
        to_email: testEmail.trim(),
        template_key: selectedKey,
      }, { headers });
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test email");
    } finally {
      setSending(false);
    }
  };

  const copyVariable = (variable) => {
    navigator.clipboard.writeText(variable);
    toast.success(`Copied ${variable}`);
  };

  useEffect(() => {
    if (showPreview && iframeRef.current && previewHtml) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{margin:0;padding:0;background:#f3f4f6;}</style></head><body>${previewHtml}</body></html>`);
        doc.close();
      }
    }
  }, [showPreview, previewHtml]);

  const syncFromEditor = () => {
    if (editorRef.current && editData) {
      setEditData(prev => ({ ...prev, html_content: editorRef.current.innerHTML }));
    }
  };

  const activeWarnings = validationWarnings.filter(w => !dismissedWarnings.has(w.variable + w.type));

  if (viewOnly) {
    return (
      <div className={`p-4 rounded-xl text-center ${dm ? "bg-blue-900/20 text-blue-300" : "bg-blue-50 text-blue-700"}`}>
        <AlertTriangle className="w-5 h-5 mx-auto mb-2" />
        <p className="text-sm font-medium">View-only access. Contact an admin to manage email templates.</p>
      </div>
    );
  }

  // ──── TEMPLATE LIST VIEW ────
  if (!selectedKey) {
    return (
      <div className="space-y-6" data-testid="email-settings-tab">
        <div className="flex items-center justify-between">
          <div>
            <h2 className={`text-xl font-bold ${dm ? "text-gray-100" : "text-gray-900"}`}>Email Settings</h2>
            <p className={`text-sm mt-1 ${dm ? "text-gray-400" : "text-gray-500"}`}>
              Manage email templates, sender identity, and scheduled messages
            </p>
          </div>
          <Badge variant="outline" className={`text-xs ${dm ? "border-gray-600 text-gray-300" : ""}`}>
            <Mail className="w-3 h-3 mr-1" /> {templates.length} Templates
          </Badge>
        </div>

        {/* Sub-tab navigation */}
        <div className={`flex gap-1 p-1 rounded-xl ${dm ? "bg-gray-800" : "bg-gray-100"}`}>
          <button onClick={() => setSubTab("templates")} className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-lg text-xs font-medium transition-all ${subTab === "templates" ? "bg-white text-gray-900 shadow-sm" : (dm ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700")}`} data-testid="subtab-templates-btn">
            <Mail className="w-3.5 h-3.5" /> Templates
          </button>
          <button onClick={() => setSubTab("scheduled")} className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-lg text-xs font-medium transition-all ${subTab === "scheduled" ? "bg-white text-gray-900 shadow-sm" : (dm ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700")}`} data-testid="subtab-scheduled-btn">
            <Calendar className="w-3.5 h-3.5" /> Scheduled
          </button>
          <button onClick={() => setSubTab("analytics")} className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-lg text-xs font-medium transition-all ${subTab === "analytics" ? "bg-white text-gray-900 shadow-sm" : (dm ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700")}`} data-testid="subtab-analytics-btn">
            <Sparkles className="w-3.5 h-3.5" /> Analytics
          </button>
          <button onClick={() => setSubTab("abtests")} className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-lg text-xs font-medium transition-all ${subTab === "abtests" ? "bg-white text-gray-900 shadow-sm" : (dm ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700")}`} data-testid="subtab-abtests-btn">
            <FlaskConical className="w-3.5 h-3.5" /> A/B Tests
          </button>
          <button onClick={() => setSubTab("engagement")} className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-lg text-xs font-medium transition-all ${subTab === "engagement" ? "bg-white text-gray-900 shadow-sm" : (dm ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700")}`} data-testid="subtab-engagement-btn">
            <Zap className="w-3.5 h-3.5" /> Scoring
          </button>
          <button onClick={() => setSubTab("reengagement")} className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-lg text-xs font-medium transition-all ${subTab === "reengagement" ? "bg-white text-gray-900 shadow-sm" : (dm ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700")}`} data-testid="subtab-reengagement-btn">
            <RefreshCcw className="w-3.5 h-3.5" /> Win-Back
          </button>
        </div>

        {subTab === "reengagement" ? (
          <ReengagementManager darkMode={dm} />
        ) : subTab === "engagement" ? (
          <EngagementScoring darkMode={dm} />
        ) : subTab === "abtests" ? (
          <ABTestManager darkMode={dm} />
        ) : subTab === "analytics" ? (
          <EmailAnalyticsDashboard darkMode={dm} />
        ) : subTab === "scheduled" ? (
          <ScheduledMessagesManager darkMode={dm} />
        ) : (
        <>

        {/* Sender Identity */}
        <Card className={`rounded-2xl border-2 ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
          <CardHeader className="pb-3">
            <CardTitle className={`text-base ${dm ? "text-gray-100" : ""}`}>Sender Identity</CardTitle>
            <CardDescription className={dm ? "text-gray-400" : ""}>
              Default sender for all outgoing emails. Individual templates can override this.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className={`flex items-center gap-4 p-4 rounded-xl ${dm ? "bg-gray-700/50" : "bg-gray-50"}`}>
              <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className={`text-sm font-semibold ${dm ? "text-gray-100" : "text-gray-900"}`}>Great Messiah Publication</p>
                <p className={`text-xs ${dm ? "text-gray-400" : "text-gray-500"}`}>info@greatmessiahpublication.win</p>
              </div>
              <Badge className="ml-auto bg-emerald-500/10 text-emerald-600 border-emerald-500/20">Verified</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Classification Legend */}
        <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
          <CardContent className="p-4">
            <p className={`text-xs font-semibold mb-3 uppercase tracking-wider ${dm ? "text-gray-400" : "text-gray-500"}`}>Trigger-Word Classifications</p>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(CLASS_STYLES).map(([key, s]) => (
                <div key={key} className={`flex items-center gap-2 p-3 rounded-xl border ${dm ? "bg-gray-700/30 border-gray-600" : "bg-white border-gray-100"}`}>
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${s.bg}`}>
                    <s.icon className={`w-3.5 h-3.5 ${s.text}`} />
                  </div>
                  <div>
                    <p className={`text-xs font-semibold ${dm ? "text-gray-200" : "text-gray-800"}`}>{s.label}</p>
                    <p className={`text-[10px] ${dm ? "text-gray-500" : "text-gray-400"}`}>{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {templates.map(t => (
              <Card
                key={t.template_key}
                className={`rounded-2xl border cursor-pointer transition-all hover:shadow-md hover:border-primary/50 ${
                  dm ? "bg-gray-800 border-gray-700 hover:bg-gray-750" : "border-gray-200 hover:bg-gray-50"
                }`}
                onClick={() => selectTemplate(t.template_key)}
                data-testid={`email-template-card-${t.template_key}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${TEMPLATE_ICONS[t.template_key] || "bg-gray-100 text-gray-600"}`}>
                      <Mail className="w-5 h-5" />
                    </div>
                    <div className="flex items-center gap-2">
                      {t.is_custom && (
                        <Badge variant="outline" className={`text-[10px] ${dm ? "border-gray-600 text-gray-300" : ""}`}>
                          Customized
                        </Badge>
                      )}
                      <Badge className={t.is_enabled
                        ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-[10px]"
                        : "bg-gray-500/10 text-gray-500 border-gray-500/20 text-[10px]"
                      }>
                        {t.is_enabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </div>
                  </div>
                  <h3 className={`font-semibold text-sm ${dm ? "text-gray-100" : "text-gray-900"}`}>{t.display_name}</h3>
                  <p className={`text-xs mt-1 truncate ${dm ? "text-gray-400" : "text-gray-500"}`}>{t.subject}</p>
                  {/* Variables badges */}
                  <div className="flex flex-wrap gap-1 mt-2.5">
                    {(t.variables || []).map(v => {
                      const cs = CLASS_STYLES[v.classification] || CLASS_STYLES.strict_fixed;
                      return (
                        <span key={v.key} className={`px-1.5 py-0.5 rounded text-[9px] font-mono border ${cs.bg} ${cs.text} ${cs.border}`}>
                          {v.key}
                        </span>
                      );
                    })}
                  </div>
                  {t.updated_at && (
                    <p className={`text-[10px] mt-2 ${dm ? "text-gray-500" : "text-gray-400"}`}>
                      Updated {new Date(t.updated_at).toLocaleDateString()}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        </>
        )}
      </div>
    );
  }

  // ──── TEMPLATE EDITOR VIEW ────
  return (
    <div className="space-y-4" data-testid="email-template-editor">
      {dm && <style>{emailEditorDarkStyle}</style>}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost" size="sm"
          onClick={() => { setSelectedKey(null); setEditData(null); setShowPreview(false); setValidationWarnings([]); }}
          className={`rounded-xl ${dm ? "text-gray-300 hover:text-white" : ""}`}
          data-testid="back-to-templates-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <h2 className={`text-lg font-bold ${dm ? "text-gray-100" : "text-gray-900"}`}>
            {editData?.display_name || "Edit Template"}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleReset} className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-200" : ""}`} data-testid="reset-template-btn">
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset Default
          </Button>
          <Button variant="outline" size="sm" onClick={handlePreview} className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-200" : ""}`} data-testid="preview-template-btn">
            <Eye className="w-3.5 h-3.5 mr-1" /> Preview
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="rounded-xl bg-[#e0482d] hover:bg-[#c93d25] text-white" data-testid="save-template-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Save Template
          </Button>
        </div>
      </div>

      {/* Validation Warnings Banner */}
      {activeWarnings.length > 0 && (
        <div className={`rounded-xl border-2 p-4 space-y-2 ${dm ? "bg-amber-950/30 border-amber-700/50" : "bg-amber-50 border-amber-200"}`} data-testid="validation-warnings-panel">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <span className={`text-sm font-semibold ${dm ? "text-amber-300" : "text-amber-800"}`}>
              {activeWarnings.length} Trigger-Word Warning{activeWarnings.length !== 1 ? "s" : ""}
            </span>
          </div>
          {activeWarnings.map((w, i) => {
            const severity = w.severity === "error" ? (dm ? "bg-red-900/30 border-red-700/50" : "bg-red-50 border-red-200") : w.severity === "warning" ? (dm ? "bg-amber-900/20 border-amber-700/40" : "bg-amber-50/80 border-amber-200") : (dm ? "bg-blue-900/20 border-blue-700/40" : "bg-blue-50 border-blue-200");
            const SevIcon = w.severity === "error" ? X : w.severity === "warning" ? AlertTriangle : Info;
            const sevColor = w.severity === "error" ? "text-red-500" : w.severity === "warning" ? "text-amber-500" : "text-blue-500";
            return (
              <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${severity}`}>
                <SevIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${sevColor}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <code className={`text-xs font-mono px-1.5 py-0.5 rounded ${dm ? "bg-gray-700 text-amber-400" : "bg-white text-amber-700"}`}>{w.variable}</code>
                    <Badge variant="outline" className={`text-[9px] ${dm ? "border-gray-600 text-gray-400" : ""}`}>
                      {w.type.replace("_", " ")}
                    </Badge>
                  </div>
                  <p className={`text-xs mt-1 ${dm ? "text-gray-300" : "text-gray-700"}`}>{w.message}</p>
                  <p className={`text-[11px] mt-0.5 italic ${dm ? "text-gray-500" : "text-gray-500"}`}>{w.suggestion}</p>
                </div>
                <button
                  onClick={() => setDismissedWarnings(prev => new Set([...prev, w.variable + w.type]))}
                  className={`p-1 rounded hover:bg-gray-200 ${dm ? "hover:bg-gray-700 text-gray-500" : "text-gray-400"}`}
                  title="Dismiss"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Left: Editor */}
        <div className="xl:col-span-2 space-y-4">
          {/* Subject + Settings */}
          <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <label className={`text-sm font-medium ${dm ? "text-gray-200" : "text-gray-700"}`}>Notification Enabled</label>
                <Switch
                  checked={editData?.is_enabled ?? true}
                  onCheckedChange={(v) => setEditData(prev => ({ ...prev, is_enabled: v }))}
                  data-testid="template-enabled-switch"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${dm ? "text-gray-200" : "text-gray-700"}`}>Subject Line</label>
                <Input
                  value={editData?.subject || ""}
                  onChange={(e) => setEditData(prev => ({ ...prev, subject: e.target.value }))}
                  className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`}
                  placeholder="Email subject..."
                  data-testid="template-subject-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-sm font-medium mb-1.5 ${dm ? "text-gray-200" : "text-gray-700"}`}>Sender Email (override)</label>
                  <Input
                    value={editData?.sender_email || ""}
                    onChange={(e) => setEditData(prev => ({ ...prev, sender_email: e.target.value }))}
                    className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`}
                    placeholder="Leave blank to use default"
                    data-testid="template-sender-email-input"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1.5 ${dm ? "text-gray-200" : "text-gray-700"}`}>Sender Name (override)</label>
                  <Input
                    value={editData?.sender_name || ""}
                    onChange={(e) => setEditData(prev => ({ ...prev, sender_name: e.target.value }))}
                    className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`}
                    placeholder="Leave blank to use default"
                    data-testid="template-sender-name-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Body Editor */}
          <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className={`text-sm ${dm ? "text-gray-100" : ""}`}>Email Body</CardTitle>
                <Tabs value={editorMode} onValueChange={setEditorMode}>
                  <TabsList className={`h-8 rounded-lg ${dm ? "bg-gray-700" : ""}`}>
                    <TabsTrigger value="visual" className="text-xs px-3 h-6 rounded-md data-[state=active]:bg-white data-[state=active]:text-gray-900">
                      <Sparkles className="w-3 h-3 mr-1" /> Visual
                    </TabsTrigger>
                    <TabsTrigger value="code" className="text-xs px-3 h-6 rounded-md data-[state=active]:bg-white data-[state=active]:text-gray-900">
                      <Code className="w-3 h-3 mr-1" /> HTML Code
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </CardHeader>
            <CardContent>
              {editorMode === "visual" ? (
                <div
                  ref={editorRef}
                  contentEditable
                  suppressContentEditableWarning
                  onBlur={syncFromEditor}
                  className={`min-h-[400px] max-h-[600px] overflow-y-auto rounded-xl border p-4 focus:outline-none focus:ring-2 focus:ring-primary/30 prose prose-sm max-w-none ${
                    dm ? "bg-gray-900 border-gray-600 prose-invert email-editor-dark" : "bg-white border-gray-200 text-gray-800"
                  }`}
                  dangerouslySetInnerHTML={{ __html: editData?.html_content || "" }}
                  data-testid="template-visual-editor"
                />
              ) : (
                <textarea
                  value={editData?.html_content || ""}
                  onChange={(e) => setEditData(prev => ({ ...prev, html_content: e.target.value }))}
                  className={`w-full min-h-[400px] max-h-[600px] rounded-xl border p-4 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                    dm ? "bg-gray-900 border-gray-600 text-green-400" : "bg-gray-50 border-gray-200 text-gray-800"
                  }`}
                  spellCheck={false}
                  data-testid="template-code-editor"
                />
              )}
            </CardContent>
          </Card>

          {/* Test Send */}
          <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <Send className={`w-4 h-4 ${dm ? "text-gray-400" : "text-gray-500"}`} />
                <span className={`text-sm font-medium ${dm ? "text-gray-200" : "text-gray-700"}`}>Send Test Email</span>
                <div className="flex-1">
                  <Input
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="recipient@example.com"
                    className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`}
                    data-testid="test-email-input"
                  />
                </div>
                <Button
                  size="sm" onClick={handleTestSend}
                  disabled={sending || !testEmail.trim()}
                  className="rounded-xl"
                  data-testid="send-test-email-btn"
                >
                  {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                  Send Test
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right: Variables + Preview */}
        <div className="space-y-4">
          {/* Variables Reference with Classifications */}
          <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-sm ${dm ? "text-gray-100" : ""}`}>
                <FileText className="w-4 h-4 inline mr-1" /> Template Variables
              </CardTitle>
              <CardDescription className={`text-xs ${dm ? "text-gray-400" : ""}`}>
                Click to copy. Use in subject or body.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {(editData?.variables || []).map(v => {
                const cs = CLASS_STYLES[v.classification] || CLASS_STYLES.strict_fixed;
                const Icon = cs.icon;
                return (
                  <button
                    key={v.key}
                    onClick={() => copyVariable(v.key)}
                    className={`w-full flex items-start gap-2.5 p-3 rounded-xl text-left transition-all hover:scale-[1.01] ${
                      dm ? "bg-gray-700/50 hover:bg-gray-700 text-gray-200" : "bg-gray-50 hover:bg-gray-100 text-gray-800"
                    }`}
                    data-testid={`var-btn-${v.key.replace(/[{}]/g, "")}`}
                  >
                    <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 ${cs.bg}`}>
                      <Icon className={`w-3 h-3 ${cs.text}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <code className={`text-xs font-mono px-1.5 py-0.5 rounded ${dm ? "bg-gray-600 text-amber-400" : "bg-amber-50 text-amber-700"}`}>
                          {v.key}
                        </code>
                        <Badge className={`text-[8px] px-1 py-0 ${cs.bg} ${cs.text} ${cs.border}`}>{cs.label}</Badge>
                      </div>
                      <p className={`text-[11px] mt-1 ${dm ? "text-gray-400" : "text-gray-500"}`}>{v.description}</p>
                    </div>
                    <Clipboard className="w-3 h-3 opacity-30 mt-1 flex-shrink-0" />
                  </button>
                );
              })}
            </CardContent>
          </Card>

          {/* Live Preview */}
          {showPreview && (
            <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`} data-testid="template-preview-card">
              <CardHeader className="pb-2">
                <CardTitle className={`text-sm ${dm ? "text-gray-100" : ""}`}>
                  <Eye className="w-4 h-4 inline mr-1" /> Preview
                </CardTitle>
                <CardDescription className={`text-xs ${dm ? "text-gray-400" : ""}`}>
                  Subject: <strong>{previewSubject}</strong>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className={`rounded-xl overflow-hidden border ${dm ? "border-gray-600" : "border-gray-200"}`}>
                  <iframe
                    ref={iframeRef}
                    title="Email Preview"
                    className="w-full bg-white"
                    style={{ height: "450px", border: "none" }}
                    sandbox="allow-same-origin"
                    data-testid="template-preview-iframe"
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
